import {Box, Button, HStack, Icon, Stack, Text, VStack, Wrap, WrapItem} from "@chakra-ui/react";
import {FilePlus2, ExternalLink, Trash2, FileText} from "lucide-react";
import React, {useCallback, useState} from "react";
import {useLanguage} from "@/contexts/languageContext";
import {toaster} from "@/components/ui/toaster";
import {useApi} from "@/uses/useApi";
import dateUtil from "@/utils/dateUtil";

export const CertificatesInput = ({certificates, userId, onRefresh}) => {

    const { t, language } = useLanguage();
    const api = useApi();
    const [isGenerating, setIsGenerating] = useState(false);

    const pollJobStatus = useCallback((jobId, onComplete) => {
        const clearLocalInterval = (callback = () => {}) => {
            setIsGenerating(false);
            clearInterval(interval);
            callback();
        }
        const interval = setInterval(async () => {
            try {
                const res = await api.ApiService.get(`user/fiscal-attestation/${jobId}`);
                const { state, result } = res.data?.value ?? {};

                if (state === 'completed') {
                    clearLocalInterval(() => onComplete(result));
                } else if (state === 'failed') {
                    clearLocalInterval(() => {
                        toaster.create({
                            title: 'Erreur',
                            description: 'La génération a échoué',
                            type: 'error',
                        });
                    });
                }
            } catch {
                clearLocalInterval();
            }
        }, 2000);

        setTimeout(() => {
            clearLocalInterval();
        }, 60_000);
    }, [api.ApiService]);

    const triggerFiscalJob = useCallback((userId) => {
        setIsGenerating(true);
        api.ApiService.create(`user/fiscal-attestations`, { userIds: [userId] })
            .then((res) => {
                if (res.data?.type === 'success') {
                    const jobId = res.data.value?.jobId;
                    pollJobStatus(jobId, (result) => {
                        onRefresh()
                    });
                }
            })
            .catch(() => {
                setIsGenerating(false);
                toaster.create({
                    title: 'Erreur',
                    description: 'Impossible de lancer la génération',
                    type: 'error',
                });
            });
    }, [api.ApiService, pollJobStatus]);

    const handleRemove = (year) => {
        api.ApiService.delete(`user/${userId}/fiscal-attestation`, year)
            .then((res) => {
                if(res?.data?.type === 'success'){
                    onRefresh()
                }
            })
            .catch()
    }

    return (
        <Stack>
            <Text fontWeight="bold" mb={2}>{t('users.certificate') || 'Certificates'} :</Text>
            <Wrap spacing={4} align="start">
                {certificates && certificates.length > 0 && certificates.map((certificate) => (
                    <WrapItem key={certificate.key}>
                        <Box
                            borderWidth="1px"
                            borderRadius="lg"
                            bg="white"
                            width="300px"
                            overflow="hidden"
                            transition="box-shadow 0.2s"
                            _hover={{ boxShadow: 'md' }}
                        >
                            <HStack
                                as="a"
                                href={certificate.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                p={4}
                                spacing={3}
                                align="center"
                                flex={1}
                                _hover={{ textDecoration: 'none' }}
                            >
                                <Box
                                    p={2}
                                    bg="blue.50"
                                    borderRadius="md"
                                    flexShrink={0}
                                >
                                    <Icon as={FileText} color="blue.500" boxSize={5} />
                                </Box>

                                <VStack align="flex-start" spacing={0.5} overflow="hidden" flex={1}>
                                    <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                                        {t('sentences.forTheYear', { value: certificate.year })}
                                    </Text>
                                    <Text fontSize="xs" color="gray.400" isTruncated w="full">
                                        {t('fields.createdAt')} {dateUtil.format(
                                        new Date(certificate?.createdAt),
                                        language === 'fr' ? 'fr-FR' : 'en-US',
                                        { day: 'numeric', month: 'numeric', year: 'numeric' },
                                        '/'
                                    )}
                                    </Text>
                                    <HStack spacing={1} w="full" overflow="hidden">
                                        <Icon as={ExternalLink} boxSize={3} color="blue.400" flexShrink={0} />
                                        <Text
                                            fontSize="xs"
                                            color="blue.400"
                                            isTruncated
                                        >
                                            {certificate.key}
                                        </Text>
                                    </HStack>
                                </VStack>
                            </HStack>

                            <HStack
                                px={4}
                                py={2}
                                borderTopWidth="1px"
                                borderColor="gray.100"
                                justify="center"
                                bg="gray.50"
                            >
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    leftIcon={<Icon as={Trash2} boxSize={4} />}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleRemove(certificate.year);
                                    }}
                                >
                                    {t('actions.delete')}
                                </Button>
                            </HStack>
                        </Box>
                    </WrapItem>

                ))}

                <WrapItem>
                    <Button
                        loading={isGenerating}
                        leftIcon={<Icon as={FilePlus2} />}
                        colorScheme="secondary"
                        onClick={() => triggerFiscalJob(userId)}
                        h="100%"
                        minH="50px"
                        backgroundColor={"secondary.500"}
                    >
                        {t('users.generateCertificate')}
                    </Button>
                </WrapItem>
            </Wrap>
        </Stack>
    );
};
